import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import api from '@/api';
import {
  isErrorCode,
  retryUnlessNotInYourVersion,
  yourVersionCourseSlug,
} from '@/api/errors';
import { QuizContent } from '@/courses/quiz/QuizContent';
import { QuizResultPanel } from '@/courses/quiz/QuizResultPanel';
import { useCourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { sameSlug } from '~/utils/slug';

export const Route = createFileRoute('/_app/_course/lessons/$lessonSlug_/quiz')(
  {
    component: LessonQuizScreen,
  },
);

function LessonQuizScreen() {
  const { lessonSlug } = Route.useParams();
  const { setSelectedLessonSlug, setExpandedLessons } = useCourseContext();
  const { me, isStudent } = useCurrentUser();
  const language = me?.language ?? 'EN';

  const { data: lesson, error: lessonError } = api.lessonBySlug.useQuery(
    { path: { slug: lessonSlug }, query: { language } },
    { retry: retryUnlessNotInYourVersion },
  );

  const lessonId = lesson?.id;

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const courseId = lessonDetail?.course_id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  // The screen is driven by server state, not by the submit response: after a
  // submit the route re-reads its state, as legacy re-renders the page, so the
  // mutation's result is discarded.
  const { data: result, error: resultError } = api.lessonQuizResult.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const state = result?.state;
  // Two states render the form. FAILED renders it under the failure panel, so
  // the retake is the form itself.
  const showsForm = state === 'not_taken' || state === 'failed';

  // Fetched only in the states that render the form, since `lessonQuestions`
  // 403s for a student who hasn't completed the topics.
  const { data: questions, error: questionsError } =
    api.lessonQuestions.useQuery(
      { path: { lesson_id: lessonId ?? 0 } },
      { enabled: lessonId != null && showsForm },
    );

  // FAILED can also be quiz-locked: `lesson_quiz_result` resolves a completed
  // attempt before the topic lock, so a student who failed and then gained a
  // new topic reads FAILED while `lessonQuestions` still 403s. The form is
  // suppressed and the panel stays; waiting on the questions would hold the
  // screen blank forever.
  //
  // The lock isn't relaxed server-side: legacy refuses the quiz whenever topics
  // are incomplete, whatever the attempts.
  const quizLocked = isErrorCode(questionsError, 'lesson_quiz_locked');
  const rendersForm = showsForm && !quizLocked;

  const { mutateAsync: submitLessonQuiz } = api.submitLessonQuiz.useMutation();

  useEffect(() => {
    if (!lessonSlug) return;
    setSelectedLessonSlug(lessonSlug);
    setExpandedLessons((prev) =>
      prev.includes(lessonSlug) ? prev : [...prev, lessonSlug],
    );
  }, [lessonSlug, setSelectedLessonSlug, setExpandedLessons]);

  const handleSubmitQuiz = async (
    answers: Record<string, string | string[] | boolean[]>,
  ) => {
    if (lessonId == null) return;
    await submitLessonQuiz({
      path: { lesson_id: lessonId },
      body: { answers: answers as any },
    });
    // Stay on the route and re-read the state, as legacy re-renders the page
    // and shows its result panels here.
    await api.lessonQuizResult.invalidateQueries();
    await api.courseProgress.invalidateQueries();
  };

  const handleRetake = () => {
    const submit = document.getElementById('saveAndContinue');
    submit?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    submit?.focus();
  };

  // A no-access quiz redirects to the parent course, as the lesson and topic
  // screens do. The 403 from `lesson_quiz_result` is the main signal; the
  // viewer-state read backs it up, and always answers `has_access` true for a
  // non-student, so staff previewing are never redirected.
  const courseSlug = courseDetail?.slug;
  const denied =
    viewerState?.has_access === false ||
    isErrorCode(resultError, 'course_access_required');
  if (denied && courseSlug !== undefined) {
    return (
      <Navigate to="/courses/$courseSlug/" params={{ courseSlug }} replace />
    );
  }

  // An old link: the viewer's version of the course has no such lesson.
  const versionCourseSlug = yourVersionCourseSlug(lessonError);
  if (versionCourseSlug) {
    return (
      <Navigate
        to="/courses/$courseSlug/"
        params={{ courseSlug: versionCourseSlug }}
        replace
      />
    );
  }

  // An old link to the same lesson in another version: show the viewer's
  // version's address.
  if (lesson && !sameSlug(lesson.slug, lessonSlug)) {
    return (
      <Navigate
        to="/lessons/$lessonSlug/quiz/"
        params={{ lessonSlug: lesson.slug }}
        replace
      />
    );
  }

  if (!lessonDetail || !result || (rendersForm && !questions)) {
    return null;
  }

  return (
    <QuizContent
      courseTitle={courseDetail?.title ?? ''}
      courseSlug={courseSlug ?? ''}
      lessonTitle={lessonDetail.title}
      lessonSlug={lessonSlug}
      quizTitle={lessonDetail.title}
      questions={rendersForm ? (questions ?? []) : []}
      showsForm={rendersForm}
      isStudent={isStudent}
      onSubmitQuiz={handleSubmitQuiz}
      panel={
        state === 'not_taken' ? null : (
          <QuizResultPanel
            result={result}
            memberId={me?.id?.toString()}
            // Retake scrolls to the form, so it's only offered when there is
            // one; a failed, quiz-locked student has none.
            onRetake={rendersForm ? handleRetake : undefined}
          />
        )
      }
    />
  );
}
